import { useEffect } from "react";

import { useNotify, useRedirect } from "react-admin";

import { InboundStatus, OutboundStatus } from "../../utils/constants";

/**
 * Only one status per workflow puts a shipment in the carrier's dispatch queue,
 * and the lists that link to the edit form filter on it. Nothing else does: a
 * bookmarked URL, the back button, or a list row rendered before the yard moved
 * the shipment on all reach the form with a record it can no longer schedule.
 *
 * The save would be rejected anyway — the schedule write is conditional on the
 * status the form loaded — but a rejection after the carrier picks a time is a
 * worse answer than not offering the scheduler at all. Send them to the
 * read-only view instead.
 *
 * Returns true while the redirect is in flight so the caller can hold the form
 * back rather than flash a scheduler the carrier cannot use.
 */
const useScheduleGuard = (record, basePath, schedulableStatus, message) => {
  const notify = useNotify();
  const redirect = useRedirect();
  const recordId = record?.id;
  const blocked = Boolean(recordId) && record.status !== schedulableStatus;

  useEffect(() => {
    if (!blocked) return;
    notify(message, "warning");
    redirect("show", basePath, recordId);
  }, [blocked, message, notify, redirect, basePath, recordId]);

  return blocked;
};

// The status and the wording it explains live together so the two carrier
// pickup forms cannot drift apart on either.
export const useOutboundScheduleGuard = (record, basePath) =>
  useScheduleGuard(
    record,
    basePath,
    OutboundStatus.PENDING,
    "This pickup is no longer available to schedule",
  );

export const useInboundScheduleGuard = (record, basePath) =>
  useScheduleGuard(
    record,
    basePath,
    InboundStatus.IN_TRANSIT,
    "This delivery is no longer available to schedule",
  );
